import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from 'reactstrap';
import { InvoiceAgingPlacementRow } from '../gql';
import {
  DtoForPlacement,
  VIEW_PLACEMENT_MANAGEMENT_ROUTE,
  VIEW_CANDIDATE_ROUTE,
  VIEW_JOB_ORDER_ROUTE,
} from '../../../../utils/constant';
import {
  computeAging,
  computeInvoiceDue,
  computeReplacement,
  bucketOf,
  formatDateUTC,
  isPastDue,
  SEVERITY,
} from '../utils/aging';

// Reactstrap Badge color by placement status — same mapping as the
// Placement Management page (src/Pages/AccountingDashboard/PlacementManagement.tsx)
// so status pills are visually consistent across the app.
function placementStatusColor(status: string | null | undefined): string {
  if (
    status === 'ACCOUNTING_APPROVED' ||
    status === 'PAID' ||
    status === 'ACCOUNTING_CONFIRMED' ||
    status === 'ACCOUNTING_INVOICED'
  )
    return 'success';
  if (status === 'PROSPECT') return 'warning';
  if (
    status === 'DRAFT' ||
    status === 'ASSIGNMENT_COMPLETED' ||
    status === 'ASSIGNMENT_CONVERTED'
  )
    return 'primary';
  if (
    status === 'REJECTED_MANAGER' ||
    status === 'REJECTED_AM' ||
    status === 'REJECTED_ACCOUNTING' ||
    status === 'REJECTED_RECRUITER' ||
    status === 'PLACEMENT_CANCELLED' ||
    status === 'ASSIGNMENT_CANCELLED'
  )
    return 'danger';
  if (
    status === 'PENDING_MANAGER' ||
    status === 'PENDING_AM' ||
    status === 'PENDING_RECRUITER' ||
    status === 'PENDING_ACCOUNTING'
  )
    return 'info';
  return 'primary';
}

// 2-decimal money for table cells
function money(amount: number | null | undefined): string {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

interface DetailTableProps {
  rows: InvoiceAgingPlacementRow[];
  today: Date;
}

const HEADERS = [
  { label: 'ID',          cls: '' },
  { label: 'Candidate',   cls: '' },
  { label: 'Job Title',   cls: '' },
  { label: 'Salary',      cls: 'align-center' },
  { label: 'Net Fee',     cls: 'align-center' },
  { label: 'Placed',      cls: 'align-center col-gap-left' },
  { label: 'Start',       cls: 'align-center' },
  { label: 'Replacement', cls: 'align-center' },
  { label: 'Inv. Due',    cls: 'align-center' },
  { label: 'Aging',       cls: 'align-center' },
  { label: 'Status',      cls: 'align-center col-gap-left' },
];

const DetailTable: FC<DetailTableProps> = ({ rows, today }) => {
  return (
    <div className="ia-table-wrap">
      <div className="ia-table">
        {/* Header */}
        <div className="ia-table-header">
          {HEADERS.map((h) => (
            <div key={h.label} className={`ia-th ${h.cls}`}>
              {h.label}
            </div>
          ))}
        </div>

        {/* Rows */}
        {rows.map((row) => {
          const isPaid = row.placementStatus === 'PAID';
          const isInvoiced = row.placementStatus === 'ACCOUNTING_INVOICED';
          const isApproved = row.placementStatus === 'ACCOUNTING_APPROVED';

          const aging = computeAging(row, today);
          const pastDue = isPastDue(row, today);
          const invDue = computeInvoiceDue(row);
          const { date: replDate, met: replMet } = computeReplacement(row, today);

          const agingBucket = aging !== null ? bucketOf(aging) : null;
          const severity = agingBucket ? SEVERITY[agingBucket] : null;

          // Invoice number: canonical is placement.invoiceNumber, fallback to attachment
          const invNum = row.invoiceNumber || row.fallbackInvoiceNumber || null;

          return (
            <div
              key={row.id}
              className={`ia-table-row${isPaid ? ' row-paid' : ''}`}
            >
              {/* 1. ID — links to placement detail */}
              <div className="ia-td">
                <Link
                  className="ia-cell-id ia-link"
                  to={`${VIEW_PLACEMENT_MANAGEMENT_ROUTE}/${row.id}`}
                >
                  {row.placementId}
                </Link>
              </div>

              {/* 2. Candidate — links to candidate detail */}
              <div className="ia-td">
                {row.candidateId ? (
                  <Link
                    className={`ia-cell-candidate ia-link${isPaid ? ' paid' : ''}`}
                    to={`${VIEW_CANDIDATE_ROUTE}/${row.candidateId}`}
                  >
                    {row.candidateName ?? '—'}
                  </Link>
                ) : (
                  <span className={`ia-cell-candidate${isPaid ? ' paid' : ''}`}>
                    {row.candidateName ?? '—'}
                  </span>
                )}
              </div>

              {/* 3. Job Title (links to job order detail) + City, State */}
              <div className="ia-td">
                <div className="ia-cell-job">
                  <div className="job-title">
                    {row.jobOrderId && row.jobTitle ? (
                      <Link
                        className="ia-link"
                        to={`${VIEW_JOB_ORDER_ROUTE}/${row.jobOrderId}`}
                      >
                        {row.jobTitle}
                      </Link>
                    ) : (
                      row.jobTitle ?? '—'
                    )}
                  </div>
                  {(row.jobCity || row.jobStateCode) && (
                    <div className="job-location">
                      {[row.jobCity, row.jobStateCode].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              </div>

              {/* 4. Salary */}
              <div className="ia-td align-center">
                <span className="ia-cell-money" style={{ color: '#6c757d' }}>
                  {money(row.salaryAmount)}
                </span>
              </div>

              {/* 5. Net Fee */}
              <div className="ia-td align-center">
                <span className="ia-cell-money ia-cell-net-fee">
                  {money(row.netCommission)}
                </span>
              </div>

              {/* 6. Placed */}
              <div className="ia-td align-center col-gap-left">
                <span className="ia-cell-date">{formatDateUTC(row.placedDate)}</span>
              </div>

              {/* 7. Start */}
              <div className="ia-td align-center">
                <span className="ia-cell-date">{formatDateUTC(row.startDate)}</span>
              </div>

              {/* 8. Replacement */}
              <div className="ia-td align-center">
                <div className="ia-cell-replacement">
                  {replDate ? (
                    <div className={`repl-date${replMet ? ' met' : ''}`}>
                      {formatDateUTC(replDate)}
                    </div>
                  ) : (
                    <span className="ia-cell-date">—</span>
                  )}
                  {replMet && <div className="repl-met-tag">✓ Met</div>}
                </div>
              </div>

              {/* 9. Inv. Due */}
              <div className="ia-td align-center">
                <div className="ia-cell-inv-due">
                  <div className="inv-date">{invDue ? formatDateUTC(invDue) : '—'}</div>
                  {(isInvoiced || isPaid) && invNum ? (
                    <div className="inv-caption inv-num">INV: {invNum}</div>
                  ) : isApproved ? (
                    <div className="inv-caption inv-pending">Pending</div>
                  ) : null}
                </div>
              </div>

              {/* 10. Aging — show the aged-days number only once past the
                  payable term; otherwise "Pending" (Paid rows show —). */}
              <div className="ia-td align-center">
                {isPaid ? (
                  <span className="ia-cell-aging aging-na">—</span>
                ) : isInvoiced && pastDue && aging !== null ? (
                  <span
                    className="ia-cell-aging"
                    style={{
                      color: severity?.fg,
                      fontWeight: 800,
                      background: severity?.bg,
                      padding: '2px 8px',
                      borderRadius: 4,
                    }}
                  >
                    {aging} days
                  </span>
                ) : (
                  <span className="ia-cell-aging aging-pending">Pending</span>
                )}
              </div>

              {/* 11. Status — same Badge + labels as Placement Management */}
              <div className="ia-td align-center col-gap-left">
                <Badge
                  color={placementStatusColor(row.placementStatus)}
                  className="text-capitalize"
                >
                  {DtoForPlacement[
                    row.placementStatus as keyof typeof DtoForPlacement
                  ] ?? '--'}
                </Badge>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DetailTable;
